import { FC } from 'react';

import { PlaceholderBar } from '../../components/loading-skeleton/index';

type ShareClassesPlaceholderProps = {
  rows?: number;
};

// Table-only skeleton — shown in the table slot while a sort/filter/search reload runs,
// so the page chrome (price, filters, search) stays put instead of flashing the full page.
// Mirrors ShareClassTable columns: name, creation date, stakeholders, rights, voting right,
// conversion ratio, seniority, liquidity pref, doc, status + actions (table/table.tsx).
export const ShareClassesTablePlaceholder: FC<ShareClassesPlaceholderProps> = ({ rows = 6 }) => (
  <div className="w-full shrink-0 overflow-x-auto">
    <table className="h-fit w-full divide-y-[1px] divide-[#F2F2F2] border-b-[1px]">
      <thead>
        <tr className="h-11 bg-gray-50">
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-24 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-24 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-14 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-28 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-24 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-10 rounded bg-gray-100" />
          </td>
          <td className="pl-4 pr-4">
            <PlaceholderBar className="h-4 w-14 rounded bg-gray-100" />
          </td>
        </tr>
      </thead>
      <tbody className="divide-y-[1px] divide-[#F2F2F2] bg-white">
        {Array.from({ length: rows }).map((_, index) => (
          <tr className="h-14" key={index}>
            <td className="p-4">
              <PlaceholderBar className="h-4 w-32 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-8 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <div className="flex flex-col gap-2">
                <PlaceholderBar className="h-3 w-20 rounded bg-gray-100" />
                <PlaceholderBar className="h-3 w-16 rounded bg-gray-100" />
              </div>
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-10 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-10 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-8 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="size-5 rounded bg-gray-100" />
            </td>
            <td className="pl-4 pr-4">
              <div className="flex items-center justify-between gap-4">
                <PlaceholderBar className="h-6 w-16 rounded-2xl bg-gray-100" />
                <PlaceholderBar className="size-5 rounded bg-gray-100" />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ShareClassesPlaceholder: FC<ShareClassesPlaceholderProps> = ({ rows = 6 }) => (
  <div className="flex h-full w-full animate-pulse flex-col overflow-hidden rounded-md bg-gray-50 p-1 shadow-sm">
    <div className="flex w-full flex-col gap-2 px-4 py-2">
      <PlaceholderBar className="h-3 w-28 rounded bg-gray-100" />
      <PlaceholderBar className="h-6 w-32 rounded bg-gray-100" />
    </div>
    <div className="flex h-full w-full flex-col rounded-md lg:border lg:border-[#F2F2F2] lg:bg-white">
      <div className="flex max-h-[68px] w-full items-center justify-between rounded-md bg-white p-4">
        <PlaceholderBar className="h-9 w-56 rounded border border-gray-200 bg-gray-100" />
        <PlaceholderBar className="h-9 w-32 rounded bg-gray-100" />
      </div>
      <ShareClassesTablePlaceholder rows={rows} />
    </div>
  </div>
);
